import { Button, ButtonV2, Search, Spinner, TooltipV2 } from '@cimplico/components';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWorkpaperContext } from '../../../../providers/WorkpaperProvider';
import { useFileRecords } from '../../../../queries/use-file-records';
import { OtherScheduleFileOverview } from '../../../../types/other-schedule';
import { WorksheetFileOverview } from '../../../../types/worksheet';
import { XMarkIcon } from '@heroicons/react/20/solid';

import AttachmentRow from './AttachmentRow';
import NewRecords from './NewRecords';
import { useFirmContext } from '../../../../providers/FirmProvider';

type AttachmentBrowserProps = {
    attachments: string[];
    defaultFolderId: string;
    worksheetFiles: WorksheetFileOverview[];
    disabled?: boolean;
    onAttach: (fileIds: string[]) => void;
    onClose: () => void;
};

function AttachmentBrowser({
    attachments,
    defaultFolderId,
    worksheetFiles,
    disabled,
    onAttach,
    onClose,
}: AttachmentBrowserProps): JSX.Element {
    const { readOnly } = useWorkpaperContext();
    const [search, setSearch] = useState('');
    const [attached, setAttached] = useState([...attachments]);
    const [currentFolderId, setCurrentFolderId] = useState(defaultFolderId);
    const filesQuery = useFileRecords(1, 500, defaultFolderId, currentFolderId, '', 'sort[dir]=asc&sort[field]=name', search);

    const workpaper = useWorkpaperContext();
    const { shortId } = useFirmContext();

    const searchRef = useRef<HTMLInputElement | undefined>();

    const currentFolder = filesQuery.data?.data.path[filesQuery.data?.data.path.length - 1];
    const previousFolder = filesQuery.data?.data.path[filesQuery.data?.data.path.length - 2];

    // Sort the records by if they are used on this entry
    const records = useMemo(() => {
        return filesQuery.data?.data.records.sort((a, b) => {
            return attached.includes(a.id) > attached.includes(b.id) ? -1 : 1;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesQuery.data?.data.records]);

    function handlePreviousFolder(): void {
        setCurrentFolderId(previousFolder!.id);
        setSearch('');
    }

    function attach(id: string): void {
        let updatedFiles: string[] = [];

        if (attached.includes(id)) {
            updatedFiles = attached.filter((p) => p !== id);
        } else {
            updatedFiles = [...attached, id];
        }

        onAttach(updatedFiles);
    }

    function handleFolderChange(id: string): void {
        setCurrentFolderId(id);
        setSearch('');
    }

    useEffect(() => {
        setAttached([...attachments]);
    }, [attachments]);

    function redirectToFiles(): void {
        if (currentFolder?.id) {
            window.open(`/${shortId}/workpapers/${workpaper.id}/files?folder=${currentFolder.id}`, '_blank');
        }
    }

    function handleCreate(id?: string): void {
        if (id) {
            attach(id);
        }
    }

    return (
        <div className="w-[400px]">
            {/* NAVIGATION */}
            <div className="px-2 py-2 h-12 border-b flex items-center">
                {previousFolder?.id && (
                    <Button type="hollow" leftIcon={<ChevronLeftIcon />} size="md" onClick={handlePreviousFolder} isSubmit={false} />
                )}
                <p className="text-sm font-medium text-gray-600 pl-2">{currentFolder?.name}</p>
                <button className="ml-auto h-5 w-5 text-gray-700" onClick={onClose} type="button">
                    <XMarkIcon />
                </button>
            </div>
            {/* FILES */}
            {filesQuery.isLoading && <LoadingFiles />}
            {filesQuery.isError && <ErrorMessage />}
            {filesQuery.isSuccess && (
                <div className={`h-[250px] relative ${filesQuery.isFetching ? 'overflow-hidden' : 'overflow-auto'}`}>
                    <div className="py-1 px-4 border-b">
                        <Search
                            placeholder="Search here and sub-folders..."
                            size="small"
                            onChange={setSearch}
                            inputRef={searchRef}
                            defaultValue={search}
                        />
                    </div>
                    {filesQuery.isFetching && (
                        <div className="absolute top-0 left-0 h-full w-full bg-white/30 flex items-center justify-center">
                            <Spinner />
                        </div>
                    )}

                    {/* NO FILES */}
                    {(!records || records.length === 0) && <p className="block text-center text-sm text-gray-500 py-8">No files</p>}
                    {records && records.length > 0 && (
                        <div>
                            {records.map((record) => {
                                // If any file has an entry
                                return (
                                    <AttachmentRow
                                        disabled={disabled}
                                        usedOnEntry={usedOnEntry(worksheetFiles, record.id)}
                                        record={record}
                                        key={record.id}
                                        defaultChecked={attached.includes(record.id)}
                                        onFolderChange={handleFolderChange}
                                        onCheckToggle={attach}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ATTACH */}
            <div className="bg-gray-50 p-2 flex justify-between items-center relative">
                <div>
                    <TooltipV2 message="View in files" position="left">
                        <ButtonV2
                            type="hollow"
                            size="sm"
                            rightIcon={<ArrowTopRightOnSquareIcon className="text-gray-600" />}
                            onClick={redirectToFiles}
                            isDisabled={!currentFolder?.id}
                        />
                    </TooltipV2>
                </div>

                <p className="mr-2 text-sm text-gray-700 absolute left-1/2 -translate-x-1/2">
                    {attached.length} {attached.length === 1 ? 'file' : 'files'} selected
                </p>

                <NewRecords folderId={currentFolderId} disabled={readOnly || disabled} onCreate={handleCreate} />
            </div>
        </div>
    );
}

function LoadingFiles(): JSX.Element {
    return (
        <div className="py-8 flex items-center justify-center">
            <Spinner />
        </div>
    );
}

function ErrorMessage(): JSX.Element {
    return <>Error!</>;
}

function usedOnEntry(files: WorksheetFileOverview[] | OtherScheduleFileOverview[], recordId: string): boolean {
    return (files.find((wf) => wf.id === recordId)?.entries || []).length > 0;
}

export default AttachmentBrowser;
